import React, { useEffect, useId, useRef, useState } from 'react';
import { composeAddress } from './addressRules';
import './addressField.css';

// M19 T7: the one address field. Type to search (Mapbox, called by our server so no token reaches the browser), pick a result, or open
// "Enter the address by hand" for street, city, state and ZIP. If search is unavailable, typing and the manual form still work.
export async function searchAddresses(client, query, fetchImpl = globalThis.fetch) {
    if (typeof fetchImpl !== 'function') return { addresses: [] };
    const { data } = await client.auth.getSession();
    const token = data?.session?.access_token;
    if (!token) return { addresses: [] };
    const response = await fetchImpl(`/api/address-search?${new URLSearchParams({ q: query })}`, { headers: { Authorization: `Bearer ${token}` } });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body?.error || 'Address search is temporarily unavailable. Enter the full address below.');
    return body;
}

export default function AddressField({ client, value, onChange, label, id, inputClassName, placeholder = 'Start typing an address', maxLength = 300, disabled = false, manualHint = true }) {
    const generated = useId();
    const inputId = id || `address-${generated}`;
    const [suggestions, setSuggestions] = useState([]);
    const [note, setNote] = useState('');
    const [loading, setLoading] = useState(false);
    const [manual, setManual] = useState(null);
    const typed = useRef(false);
    const listId = `${inputId}-list`;

    useEffect(() => {
        const query = String(value || '').trim();
        if (!typed.current || query.length < 3 || !client) { setSuggestions([]); setLoading(false); return undefined; }
        let live = true;
        const timer = setTimeout(async () => {
            setLoading(true);
            try {
                const result = await searchAddresses(client, query);
                if (live) { setSuggestions(result.addresses || []); setNote(result.unavailable || ''); }
            } catch (error) {
                if (live) { setSuggestions([]); setNote(error.message); }
            } finally { if (live) setLoading(false); }
        }, 300);
        return () => { live = false; clearTimeout(timer); };
    }, [client, value]);

    const pick = (item) => { typed.current = false; setSuggestions([]); setNote(''); setManual(null); onChange(item.label, item); };
    const editManual = (patch) => { const next = { ...manual, ...patch }; setManual(next); onChange(composeAddress(next)); };

    return (
        <div className="af-address">
            <input id={inputId} type="text" role="combobox" aria-label={label} aria-autocomplete="list" aria-expanded={suggestions.length > 0} aria-controls={listId}
                value={value || ''} maxLength={maxLength} placeholder={placeholder} autoComplete="off" disabled={disabled} className={inputClassName} readOnly={Boolean(manual)}
                onChange={(event) => { typed.current = true; onChange(event.target.value); }} />
            {loading && <p role="status" className="af-address__note">Searching addresses…</p>}
            {note && <p className="af-address__note">{note}</p>}
            <ul id={listId} role="listbox" aria-label={`${label} suggestions`} className="af-address__list" hidden={!suggestions.length}>
                {suggestions.map((item) => (
                    <li key={item.id || item.label} role="option" aria-selected="false">
                        <button type="button" className="af-address__option" onClick={() => pick(item)}>{item.label}</button>
                    </li>
                ))}
            </ul>
            {manualHint && !disabled && (manual
                ? (<div className="af-address__manual" data-testid="address-manual">
                    <label>Street<input value={manual.street} onChange={(e) => editManual({ street: e.target.value })} autoComplete="off" /></label>
                    <label>City<input value={manual.city} onChange={(e) => editManual({ city: e.target.value })} autoComplete="off" /></label>
                    <label>State<input value={manual.state} maxLength={30} onChange={(e) => editManual({ state: e.target.value })} autoComplete="off" /></label>
                    <label>ZIP<input value={manual.zip} maxLength={10} inputMode="numeric" onChange={(e) => editManual({ zip: e.target.value })} autoComplete="off" /></label>
                    <button type="button" className="af-address__toggle" onClick={() => setManual(null)}>Use the search instead</button>
                </div>)
                : <button type="button" className="af-address__toggle" onClick={() => { setSuggestions([]); setManual({ street: '', city: '', state: '', zip: '' }); onChange(''); }}>Enter the address by hand</button>)}
        </div>
    );
}
